import {
  calibrationDeviceBranchState,
  calibrationDeviceTreeRows,
  type visibleCalibrationDevicePaginationCandidate,
} from '@/features/calibration/lib/calibrationDeviceTree';
import { calibrationDevicesQuery } from '@/features/calibration/services/calibrationQueries';
import type { CalibrationStation } from '@/features/calibration/types/calibration';
import type {
  CalibrationDeviceBranch,
  CalibrationDeviceTreeNode,
} from '@/features/calibration/types/calibrationDeviceTree';
import { useInfiniteQueryObservers } from '@/shared/hooks/useInfiniteQueryObservers';
import {
  flattenInfiniteItems,
  shouldFetchNextVirtualPageAtIndex,
} from '@/shared/lib/infiniteQuery';
import { useCallback, useMemo } from 'react';

export function useCalibrationDeviceTree(
  stations: readonly CalibrationStation[],
  expandedStationIds: ReadonlySet<string>,
) {
  const expandedStations = useMemo(
    () => stations.filter((station) => expandedStationIds.has(station.id)),
    [expandedStationIds, stations],
  );
  const observerEntries = useMemo(
    () =>
      expandedStations.map((station) => ({
        key: station.id,
        options: calibrationDevicesQuery(station.id),
      })),
    [expandedStations],
  );
  const deviceQueries = useInfiniteQueryObservers(observerEntries);
  const nodes = useMemo<CalibrationDeviceTreeNode[]>(() => {
    return stations.map((station) => {
      const query = deviceQueries.get(station.id);
      const devices = flattenInfiniteItems(query?.data);
      let branch: CalibrationDeviceBranch | undefined;
      if (expandedStationIds.has(station.id)) {
        const state = calibrationDeviceBranchState({
          hasData: query?.data !== undefined,
          isError: query?.isError ?? false,
          isPending: query?.isPending ?? true,
          deviceCount: devices.length,
        });
        branch = {
          state,
          devices,
          hasNextPage: Boolean(query?.hasNextPage),
          showFooter:
            state === 'ready' && Boolean(query?.isFetchingNextPage || query?.isFetchNextPageError),
        };
      }
      return { station, expanded: expandedStationIds.has(station.id), branch };
    });
  }, [deviceQueries, expandedStationIds, stations]);
  const rows = useMemo(() => calibrationDeviceTreeRows(nodes), [nodes]);
  const getDeviceQuery = useCallback(
    (stationId: string) => deviceQueries.get(stationId),
    [deviceQueries],
  );
  const retryDeviceBranch = useCallback(
    (stationId: string) => {
      void deviceQueries.get(stationId)?.refetch();
    },
    [deviceQueries],
  );
  const fetchNextDevicePage = useCallback(
    (
      candidate: ReturnType<typeof visibleCalibrationDevicePaginationCandidate>,
      isScrolling: boolean,
    ) => {
      if (!candidate) return;
      const query = deviceQueries.get(candidate.stationId);
      if (
        query &&
        shouldFetchNextVirtualPageAtIndex({
          isScrolling,
          itemCount: candidate.deviceCount,
          lastVisibleIndex: candidate.deviceIndex,
          hasNextPage: query.hasNextPage,
          isFetchingNextPage: query.isFetchingNextPage,
          isFetchNextPageError: query.isFetchNextPageError,
        })
      )
        void query.fetchNextPage();
    },
    [deviceQueries],
  );
  return { rows, getDeviceQuery, retryDeviceBranch, fetchNextDevicePage };
}
